// signals-web.ts — the WEB runtime's derived focus signals.
//
// 🔴 WHY THIS EXISTS: `weekly_signals` was a zero stub in web-commands.ts. On web every figure came
// back 0, so the SignalStrip (including "Peak time") never rendered at all AND the billed weekly
// summary was handed an object with no sessions in it — we charged for a model to describe data it
// could not see. Native is unaffected (Rust computes this in db.rs). Ledger row 2c.
//
// Every rule below mirrors `src-tauri/src/db.rs::weekly_signals` and its helpers. Where the two
// could drift, THE RUST WINS — it is the shipping surface on iOS and macOS.
//
// ⚠️ OBSERVATIONAL, NOT MOTIVATIONAL. The Rust facts state what the record shows ("Most of your
// sessions started in the evenings"), never advice, causation or a good/bad verdict. Keep that
// voice — this is someone's own log, not a coach.
//
// ⚠️ LOCAL TIME, like the Rust (`with_timezone(&chrono::Local)`). Bucketing by UTC here would put
// a session in a different band from the one the native app shows for the same row, and from the
// "When you focused" grid in App.tsx which mirrors the same boundaries.

import type { FocusSession, WeeklySignals, WeekTotals, HeadlineFact, FactTone } from "./store";

const DAY_MS = 86400000;

// Rust: (x * 100.0).round() / 100.0, and 0.0 for non-finite.
const round2 = (x: number): number => (Number.isFinite(x) ? Math.round(x * 100) / 100 : 0);

const localDateOf = (startedAt: string): string | null => {
  const d = new Date(startedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear().toString().padStart(4, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Same boundaries as db.rs::band_of and as the FocusGrid in App.tsx.
const bandOf = (startedAt: string): string | null => {
  const d = new Date(startedAt);
  if (Number.isNaN(d.getTime())) return null;
  const h = d.getHours();
  if (h >= 5 && h <= 11) return "morning";
  if (h >= 12 && h <= 16) return "afternoon";
  if (h >= 17 && h <= 21) return "evening";
  return "night";
};

const bandPhrase = (b: string): string =>
  ({ morning: "mornings", afternoon: "afternoons", evening: "evenings", night: "late nights" } as Record<string, string>)[b] ?? "—";

const fmtHour = (h: number): string => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "am" : "pm"}`;

// Population standard deviation (we hold the full set, not a sample). 0 for <2 values — same as Rust.
function stdDev(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = xs.reduce((a, v) => a + v, 0) / xs.length;
  return Math.sqrt(xs.reduce((a, v) => a + (v - m) ** 2, 0) / xs.length);
}

const totalsOf = (rows: FocusSession[]): WeekTotals => {
  const sessions = rows.length;
  const completed = rows.filter((s) => s.completed_at != null).length;
  const total_minutes = rows.reduce((a, s) => a + s.duration_min, 0);
  return {
    sessions, completed, total_minutes,
    avg_duration_min: round2(sessions > 0 ? total_minutes / sessions : 0),
  };
};

/**
 * Mirrors db.rs::weekly_signals(days). `all` is every stored session; the windows are cut here
 * exactly as the Rust cuts them with SQL (current = now-days .. now, prior = the window before).
 */
export function weeklySignalsWeb(all: FocusSession[], daysArg: number): WeeklySignals {
  const days = Math.max(1, daysArg);
  const now = Date.now();
  const winMs = days * DAY_MS;
  const curStart = now - winMs;
  const priorStart = now - winMs - winMs;

  const at = (s: FocusSession) => new Date(s.started_at).getTime();
  const inRange = (s: FocusSession, from: number, to: number) => {
    const t = at(s);
    return Number.isFinite(t) && t >= from && t < to;
  };
  // Rust reads them ascending by started_at; the trend slope depends on that order.
  const byTime = (a: FocusSession, b: FocusSession) => at(a) - at(b);
  const current = all.filter((s) => inRange(s, curStart, now + 1)).sort(byTime);
  const prior = all.filter((s) => inRange(s, priorStart, curStart)).sort(byTime);

  const this_week = totalsOf(current);
  const prior_week = totalsOf(prior);
  const week_over_week = {
    this_week, prior_week,
    sessions_delta: this_week.sessions - prior_week.sessions,
    minutes_delta: this_week.total_minutes - prior_week.total_minutes,
    minutes_pct_change: prior_week.total_minutes > 0
      ? round2(((this_week.total_minutes - prior_week.total_minutes) / prior_week.total_minutes) * 100)
      : null,
  };

  const sessions_total = current.length;
  const completed_total = current.filter((s) => s.completed_at != null).length;
  const completion_rate = round2(sessions_total > 0 ? completed_total / sessions_total : 0);
  const total_focus_minutes = current.reduce((a, s) => a + s.duration_min, 0);
  const avg_duration_min = round2(sessions_total > 0 ? total_focus_minutes / sessions_total : 0);

  // Hour histogram + bands + distinct active days (local).
  const hist = new Array(24).fill(0) as number[];
  const activeDays = new Set<string>();
  let morning = 0, afternoon = 0, evening = 0, night = 0;
  for (const s of current) {
    const d = new Date(s.started_at);
    if (Number.isNaN(d.getTime())) continue;
    const h = d.getHours();
    hist[h] += 1;
    const ld = localDateOf(s.started_at);
    if (ld) activeDays.add(ld);
    if (h >= 5 && h <= 11) morning++;
    else if (h >= 12 && h <= 16) afternoon++;
    else if (h >= 17 && h <= 21) evening++;
    else night++;
  }
  // Rust: max_by_key over non-zero counts. Rust's max_by_key returns the LAST maximum on ties.
  let peak_hour: number | null = null;
  for (let h = 0; h < 24; h++) if (hist[h] > 0 && (peak_hour === null || hist[h] >= hist[peak_hour])) peak_hour = h;
  const bandCounts: [string, number][] = [["morning", morning], ["afternoon", afternoon], ["evening", evening], ["night", night]];
  let peak_band = "none";
  let peakBandCount = 0;
  for (const [name, c] of bandCounts) if (c > 0 && c >= peakBandCount) { peak_band = name; peakBandCount = c; }
  const hour_clustering = { morning, afternoon, evening, night, histogram_24h: hist, peak_band, peak_hour };

  // Reflections — counts and LENGTHS only. The text itself never leaves the device.
  const lengths = current
    .filter((s) => s.reflection != null)
    .map((s) => [...String(s.reflection)].length); // chars().count() counts code points
  const with_reflection = lengths.length;
  const reflections = {
    sessions_total,
    with_reflection,
    reflection_rate: round2(sessions_total > 0 ? with_reflection / sessions_total : 0),
    avg_length_chars: round2(with_reflection > 0 ? lengths.reduce((a, n) => a + n, 0) / with_reflection : 0),
    max_length_chars: lengths.length ? Math.max(...lengths) : 0,
  };

  const durations_offered = [...new Set(current.map((s) => s.duration_min))].sort((a, b) => a - b);

  // Least-squares slope of duration over chronological index.
  const n = current.length;
  const first_duration_min = n ? current[0].duration_min : null;
  const last_duration_min = n ? current[n - 1].duration_min : null;
  let slope = 0;
  if (n >= 2) {
    const meanX = (n - 1) / 2;
    const meanY = current.reduce((a, s) => a + s.duration_min, 0) / n;
    let num = 0, den = 0;
    current.forEach((s, i) => { const dx = i - meanX; num += dx * (s.duration_min - meanY); den += dx * dx; });
    slope = den !== 0 ? num / den : 0;
  }
  const duration_trend = { slope_min_per_session: round2(slope), first_duration_min, last_duration_min, n };

  // The LONG LENS: streaks and weekday rhythm need more than the 7-day window can show.
  const horizon_days = 56;
  const horizon = all.filter((s) => inRange(s, now - horizon_days * DAY_MS, now + 1)).sort(byTime);

  const horizonDays = new Set<string>();
  for (const s of horizon) { const d = localDateOf(s.started_at); if (d) horizonDays.add(d); }
  const sortedDays = [...horizonDays].sort();
  let longest_streak = 0, run = 0;
  let prevMs: number | null = null;
  for (const d of sortedDays) {
    const ms = Date.parse(`${d}T00:00:00`);
    run = prevMs !== null && Math.round((ms - prevMs) / DAY_MS) === 1 ? run + 1 : 1;
    if (run > longest_streak) longest_streak = run;
    prevMs = ms;
  }
  // Current streak = consecutive days ending TODAY. 0 if nothing logged today (same as Rust).
  let current_streak = 0;
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  for (;;) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
    if (!horizonDays.has(key)) break;
    current_streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  // Busiest single day in the CURRENT window: most sessions, minutes as the tiebreak.
  const perDay = new Map<string, { n: number; mins: number }>();
  for (const s of current) {
    const d = localDateOf(s.started_at);
    if (!d) continue;
    const e = perDay.get(d) ?? { n: 0, mins: 0 };
    e.n += 1; e.mins += s.duration_min;
    perDay.set(d, e);
  }
  let busiest_day: string | null = null, busiest_day_sessions = 0, busiest_day_minutes = 0;
  for (const [d, e] of perDay) {
    if (e.n > busiest_day_sessions || (e.n === busiest_day_sessions && e.mins > busiest_day_minutes)) {
      busiest_day = d; busiest_day_sessions = e.n; busiest_day_minutes = e.mins;
    }
  }
  const active_day_streaks = { current_streak, longest_streak, busiest_day, busiest_day_sessions, busiest_day_minutes };

  const curDur = current.map((s) => s.duration_min);
  const dur_std = stdDev(curDur.map(Number));
  const dur_min = curDur.length ? Math.min(...curDur) : 0;
  const dur_max = curDur.length ? Math.max(...curDur) : 0;
  const duration_volatility = {
    mean_min: round2(curDur.length ? curDur.reduce((a, v) => a + v, 0) / curDur.length : 0),
    std_dev_min: round2(dur_std),
    min_min: dur_min,
    max_min: dur_max,
    range_min: dur_max - dur_min,
    label: current.length < 2 || dur_std < 6 ? "steady" : dur_std < 22 ? "mixed" : "varied",
  };

  // Completion by band — ordered by session count, strongest/weakest only when ≥2 comparable
  // bands (≥2 sessions each) genuinely differ. Rust refuses to name a "best" time off one session.
  const bandAcc = new Map<string, { sessions: number; completed: number }>();
  for (const s of current) {
    const b = bandOf(s.started_at);
    if (!b) continue;
    const e = bandAcc.get(b) ?? { sessions: 0, completed: 0 };
    e.sessions += 1;
    if (s.completed_at != null) e.completed += 1;
    bandAcc.set(b, e);
  }
  const bands = (["morning", "afternoon", "evening", "night"] as const)
    .filter((b) => bandAcc.has(b))
    .map((b) => {
      const e = bandAcc.get(b)!;
      return { band: b as string, sessions: e.sessions, completed: e.completed, completion_rate: round2(e.sessions > 0 ? e.completed / e.sessions : 0) };
    })
    .sort((a, b) => b.sessions - a.sessions);
  const comparable = bands.filter((b) => b.sessions >= 2);
  let strongest_band: string | null = null, strongest_rate: number | null = null;
  let weakest_band: string | null = null, weakest_rate: number | null = null;
  if (comparable.length >= 2) {
    const s = comparable.reduce((a, b) => (b.completion_rate > a.completion_rate ? b : a));
    const w = comparable.reduce((a, b) => (b.completion_rate < a.completion_rate ? b : a));
    if (Math.abs(s.completion_rate - w.completion_rate) > 0.001) {
      strongest_band = s.band; strongest_rate = s.completion_rate;
      weakest_band = w.band; weakest_rate = w.completion_rate;
    }
  }
  const completion_by_band = { bands, strongest_band, strongest_rate, weakest_band, weakest_rate };

  // Weekday rhythm over the horizon. Rust indexes num_days_from_monday(); JS getDay() is Sun=0.
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const acc = labels.map(() => ({ sessions: 0, minutes: 0, completed: 0 }));
  for (const s of horizon) {
    const d = new Date(s.started_at);
    if (Number.isNaN(d.getTime())) continue;
    const idx = (d.getDay() + 6) % 7;
    acc[idx].sessions += 1;
    acc[idx].minutes += s.duration_min;
    if (s.completed_at != null) acc[idx].completed += 1;
  }
  const wdays = labels.map((label, i) => ({
    weekday: label,
    sessions: acc[i].sessions,
    total_minutes: acc[i].minutes,
    completion_rate: acc[i].sessions > 0 ? round2(acc[i].completed / acc[i].sessions) : null,
  }));
  let busiestIdx = 0;
  for (let i = 1; i < 7; i++) if (acc[i].sessions > acc[busiestIdx].sessions) busiestIdx = i;
  const weekday_pattern = {
    horizon_days,
    days: wdays,
    busiest_weekday: acc[busiestIdx].sessions > 0 ? labels[busiestIdx] : null,
    busiest_weekday_sessions: acc[busiestIdx].sessions,
  };

  // ── Headline facts: (salience, kind, tone, text), sorted then ranked. Mirrors the 11 rules and
  // their thresholds exactly — the thresholds are what keep a thin week from being narrated as if
  // it meant something.
  const scored: { sal: number; kind: string; tone: FactTone; text: string }[] = [];
  if (sessions_total > 0) {
    scored.push({
      sal: 2.0, kind: "volume", tone: "neutral",
      text: `Over the last ${days} days you logged ${sessions_total} focus session${sessions_total === 1 ? "" : "s"} totalling ${total_focus_minutes} minutes, finishing ${completed_total} of them (${Math.round(completion_rate * 100)}%).`,
    });

    if (prior_week.sessions > 0 || this_week.sessions > 0) {
      const d = week_over_week.minutes_delta;
      if (Math.abs(d) >= 5) {
        const p = week_over_week.minutes_pct_change;
        const pctPart = p !== null && Math.abs(p) >= 1 ? ` (${p > 0 ? "+" : ""}${Math.round(p)}%)` : "";
        scored.push({
          sal: 2.6 + Math.abs(d) / 60, kind: "week_over_week", tone: d > 0 ? "rising" : "notable",
          text: `That's ${Math.abs(d)} minutes ${d > 0 ? "up" : "down"}${pctPart} from the ${prior_week.total_minutes} minutes you logged the week before.`,
        });
      } else if (prior_week.total_minutes > 0) {
        scored.push({
          sal: 1.0, kind: "week_over_week", tone: "steady",
          text: `Your focus minutes held about level with last week (${this_week.total_minutes} vs ${prior_week.total_minutes}).`,
        });
      }
    }

    if (current_streak >= 2) {
      scored.push({
        sal: 2.4 + current_streak * 0.15, kind: "streak", tone: "rising",
        text: `You've focused on ${current_streak} days in a row right up to today.`,
      });
    } else if (longest_streak >= 3) {
      scored.push({
        sal: 1.1, kind: "streak", tone: "neutral",
        text: `Your longest run was ${longest_streak} days in a row with at least one session.`,
      });
    }

    if (peak_hour !== null && sessions_total >= 3 && peak_band !== "none") {
      scored.push({
        sal: 1.5, kind: "peak_time", tone: "notable",
        text: `Most of your sessions started in the ${bandPhrase(peak_band)} — the ${fmtHour(peak_hour)} hour was your busiest.`,
      });
    }

    if (strongest_band && strongest_rate !== null && weakest_band && weakest_rate !== null) {
      scored.push({
        sal: 1.4 + Math.abs(strongest_rate - weakest_rate), kind: "band_completion", tone: "notable",
        text: `Sessions you started in the ${bandPhrase(strongest_band)} ran to completion ${Math.round(strongest_rate * 100)}% of the time, versus ${Math.round(weakest_rate * 100)}% for ${bandPhrase(weakest_band)}.`,
      });
    }

    if (duration_trend.n >= 3 && Math.abs(duration_trend.slope_min_per_session) >= 0.1
        && first_duration_min !== null && last_duration_min !== null) {
      const up = duration_trend.slope_min_per_session > 0;
      scored.push({
        sal: 1.3 + Math.abs(duration_trend.slope_min_per_session), kind: "length_trend", tone: up ? "rising" : "notable",
        text: `Across the week your sessions ${up ? "lengthened" : "shortened"}, going from ${first_duration_min} to ${last_duration_min} minutes end to end.`,
      });
    }

    if (weekday_pattern.busiest_weekday && weekday_pattern.busiest_weekday_sessions >= 3) {
      scored.push({
        sal: 0.9, kind: "weekday_rhythm", tone: "neutral",
        text: `Looking back ${horizon_days} days, ${weekday_pattern.busiest_weekday} carried the most sessions (${weekday_pattern.busiest_weekday_sessions}).`,
      });
    }

    if (sessions_total >= 3 && duration_volatility.label !== "steady") {
      scored.push({
        sal: 0.7, kind: "length_spread", tone: "neutral",
        text: `Session length ran ${duration_volatility.label} this week, spanning ${duration_volatility.min_min} to ${duration_volatility.max_min} minutes (avg ${Math.round(duration_volatility.mean_min)}).`,
      });
    }

    if (busiest_day && busiest_day_sessions >= 2) {
      scored.push({
        sal: 0.6, kind: "busiest_day", tone: "notable",
        text: `Your heaviest day was ${busiest_day} with ${busiest_day_sessions} sessions and ${busiest_day_minutes} focus minutes.`,
      });
    }

    if (activeDays.size >= 1) {
      scored.push({
        sal: 0.5, kind: "coverage", tone: "steady",
        text: `You ran a session on ${activeDays.size} of the last ${days} calendar days, averaging ${Math.round(avg_duration_min)} minutes each.`,
      });
    }

    if (with_reflection >= 2) {
      scored.push({
        sal: 0.4, kind: "reflections", tone: "neutral",
        text: `You wrote a reflection on ${with_reflection} of ${sessions_total} sessions (the text stays on this device).`,
      });
    }
  }
  const headline_facts: HeadlineFact[] = scored
    .sort((a, b) => b.sal - a.sal)
    .map((f, i) => ({ rank: i, kind: f.kind, tone: f.tone, text: f.text }));

  return {
    // ⚠️ VERBATIM from db.rs. This is not a version tag — it is the instruction envelope the
    // server hands the model (no causation, lean on headline_facts, nothing here is advice).
    // Changing a word here changes what the AI is told, on web only, silently.
    schema:
      "solo_focus_log.weekly_signals.v2: derived aggregates only — counts, rates, " +
      "histograms, totals, a duration slope, streaks, std-devs, and pre-ranked " +
      "headline_facts. NO raw session rows and NO reflection text leave the device. " +
      "Every figure is a plain observation computed on the user's own data. " +
      "headline_facts are ranked, ready-to-narrate observational sentences — lean on " +
      "them and do NOT contradict them. Describe these numbers factually: clustering " +
      "and co-occurrence are co-occurrence, NEVER causation, and nothing here is advice.",
    window: {
      days,
      start: new Date(curStart).toISOString(),
      end: new Date(now).toISOString(),
      generated_at: new Date(now).toISOString(),
    },
    sessions_total,
    completed_total,
    completion_rate,
    total_focus_minutes,
    avg_duration_min,
    distinct_active_days: activeDays.size,
    durations_offered,
    hour_clustering,
    reflections,
    week_over_week,
    duration_trend,
    active_day_streaks,
    duration_volatility,
    completion_by_band,
    weekday_pattern,
    headline_facts,
  };
}
